import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface NavigationStyleEditorProps {
  navMainBg: string;
  navMainBorder: string;
  navTopBg: string;
  navTopBorder: string;
  navTopText: string;
  navBottomBg: string;
  navBottomBorder: string;
  navLogoText: string;
  navSliderBg: string;
  navSliderText: string;
  navSearchButtonBg: string;
  navSearchButtonText: string;
  onNavMainBgChange: (color: string) => void;
  onNavMainBorderChange: (color: string) => void;
  onNavTopBgChange: (color: string) => void;
  onNavTopBorderChange: (color: string) => void;
  onNavTopTextChange: (color: string) => void;
  onNavBottomBgChange: (color: string) => void;
  onNavBottomBorderChange: (color: string) => void;
  onNavLogoTextChange: (color: string) => void;
  onNavSliderBgChange: (color: string) => void;
  onNavSliderTextChange: (color: string) => void;
  onNavSearchButtonBgChange: (color: string) => void;
  onNavSearchButtonTextChange: (color: string) => void;
  primaryColor: string;
  secondaryColor: string;
}

/**
 * NavigationStyleEditor - Comprehensive navigation styling editor for all navigation components
 */
const NavigationStyleEditor: React.FC<NavigationStyleEditorProps> = ({
  navMainBg,
  navMainBorder,
  navTopBg,
  navTopBorder,
  navTopText,
  navBottomBg,
  navBottomBorder,
  navLogoText,
  navSliderBg,
  navSliderText,
  navSearchButtonBg,
  navSearchButtonText,
  onNavMainBgChange,
  onNavMainBorderChange,
  onNavTopBgChange,
  onNavTopBorderChange,
  onNavTopTextChange,
  onNavBottomBgChange,
  onNavBottomBorderChange,
  onNavLogoTextChange,
  onNavSliderBgChange,
  onNavSliderTextChange,
  onNavSearchButtonBgChange,
  onNavSearchButtonTextChange,
  primaryColor,
  secondaryColor
}) => {
  const colorPresets = [
    { name: 'Primary', color: primaryColor },
    { name: 'Secondary', color: secondaryColor },
    { name: 'White', color: '#ffffff' },
    { name: 'Black', color: '#2D2D2D' },
    { name: 'Transparent', color: 'transparent' },
    { name: 'Gold', color: '#c5b068' },
    { name: 'Blue', color: '#3f92ff' },
    { name: 'Green', color: '#3fc060' }
  ];

  const resetToDefaults = () => {
    onNavMainBgChange(primaryColor);
    onNavMainBorderChange(secondaryColor);
    onNavTopBgChange(primaryColor);
    onNavTopBorderChange(secondaryColor);
    onNavTopTextChange('#ffffff');
    onNavBottomBgChange(primaryColor);
    onNavBottomBorderChange(secondaryColor);
    onNavLogoTextChange('#ffffff');
    onNavSliderBgChange(primaryColor);
    onNavSliderTextChange('#ffffff');
    onNavSearchButtonBgChange(secondaryColor);
    onNavSearchButtonTextChange('#ffffff');
  };

  const renderColorPicker = (
    label: string,
    value: string,
    onChange: (color: string) => void
  ) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start gap-2">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: value === 'transparent' ? '#f0f0f0' : value }}
              />
              {value}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-3">
              <Input
                type="color"
                value={value === 'transparent' ? '#ffffff' : value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-10"
              />
              <Input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#ffffff"
                className="font-mono text-sm"
              />
              <div className="grid grid-cols-2 gap-1">
                {colorPresets.map((preset) => (
                  <Button
                    key={preset.name}
                    variant="outline"
                    size="sm"
                    onClick={() => onChange(preset.color)}
                    className="justify-start gap-2 h-auto p-2"
                  >
                    <div 
                      className="w-3 h-3 rounded border border-gray-300"
                      style={{ backgroundColor: preset.color === 'transparent' ? '#f0f0f0' : preset.color }}
                    />
                    <span className="text-xs">{preset.name}</span>
                  </Button>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Navigation Styling</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Navigation Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">Main Navigation Bar</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderColorPicker('Background Colour', navMainBg, onNavMainBgChange)}
            {renderColorPicker('Border Colour', navMainBorder, onNavMainBorderChange)}
          </div>
        </div>

        {/* Top Bar Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">Top Bar (Language/Login)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderColorPicker('Background Colour', navTopBg, onNavTopBgChange)}
            {renderColorPicker('Border Colour', navTopBorder, onNavTopBorderChange)}
          </div>
          <div className="grid grid-cols-1 gap-4">
            {renderColorPicker('Text Colour', navTopText, onNavTopTextChange)}
          </div>
        </div>

        {/* Logo Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">Logo & Cart Button</h4>
          <div className="grid grid-cols-1 gap-4">
            {renderColorPicker('Text Colour', navLogoText, onNavLogoTextChange)}
          </div>
        </div>

        {/* Bottom Navigation Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">Bottom Navigation (Mobile)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderColorPicker('Background Colour', navBottomBg, onNavBottomBgChange)}
            {renderColorPicker('Border Colour', navBottomBorder, onNavBottomBorderChange)}
          </div>
        </div>

        {/* Slider Menu Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">Side Menu & Category Tabs</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderColorPicker('Background Colour', navSliderBg, onNavSliderBgChange)}
            {renderColorPicker('Text Colour', navSliderText, onNavSliderTextChange)}
          </div>
        </div>

        {/* Search Button Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 border-b pb-2">Search Button</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderColorPicker('Background Colour', navSearchButtonBg, onNavSearchButtonBgChange)}
            {renderColorPicker('Text/Icon Colour', navSearchButtonText, onNavSearchButtonTextChange)}
          </div>
        </div>

        {/* Reset Button */}
        <div className="pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={resetToDefaults}
            className="w-full"
          >
            Reset to Defaults
          </Button>
        </div>

        {/* Preview Section */}
        <div className="mt-6 p-4 bg-gray-100 rounded-md">
          <Label className="text-sm font-medium mb-3 block">Colour Preview</Label>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex gap-2 items-center">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: navMainBg }}
              />
              <span className="text-xs">Main Nav</span>
            </div>
            <div className="flex gap-2 items-center">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: navTopBg }}
              />
              <span className="text-xs">Top Bar</span>
            </div>
            <div className="flex gap-2 items-center">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: navBottomBg }}
              />
              <span className="text-xs">Bottom Nav</span>
            </div>
            <div className="flex gap-2 items-center">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: navSliderBg }}
              />
              <span className="text-xs">Side Menu</span>
            </div>
            <div className="flex gap-2 items-center">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: navSearchButtonBg }}
              />
              <span className="text-xs">Search Button</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NavigationStyleEditor;